import { useState, useMemo, useCallback } from 'react';
import {
  Box, Stack, Chip,
  Tabs, Tab,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useTranslation } from 'react-i18next';
import { TABLE_COLUMNS, SEARCH_CONFIGS, ROW_ACTIONS } from '../../constants/tableConfigurations';
import { DataTableContainer } from '../../components';
import { buildHasuraWhere } from '../../utils/buildHasuraWhere';

const getStatus = (dateStr) => {
  if (!dateStr) return 'pending';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  if (d.getTime() === today.getTime()) return 'today';
  if (d < today) return 'due';
  return 'pending';
};

const RemindersTab = ({ collections, loading, onFirmClick }) => {

  const allReminders = useMemo(() => {
    const list = [];
    collections.forEach(c => {
      (c.reminders || []).forEach(r => list.push({
        ...r,
        firm_name: c.firm_name,
        firm_short_name: c.firm_short_name,
        status: getStatus(r.reminder_date),
        _collection: c,
      }));
    });
    return list.sort((a, b) => new Date(a.reminder_date) - new Date(b.reminder_date));
  }, [collections]);

  return (
    <DataTableContainer
      title=""
      columns={TABLE_COLUMNS.reminders}
      data={allReminders}
      loading={loading}
      showAddButton={false}
      exportButton={false}
      searchPlaceholder={SEARCH_CONFIGS.reminders.placeholder}
      searchFields={SEARCH_CONFIGS.reminders.fields}
      showFilters={false}
      rowActions={[]}
      hideDefaultActions={true}
      entityType="reminder"
      onRowClick={(row) => onFirmClick(row._collection)}
    />
  );
};

const PaymentPromisesTab = ({ collections, loading, onFirmClick }) => {

  const allPromises = useMemo(() => {
    const list = [];
    collections.forEach(c => {
      (c.payment_promises || []).forEach(p => list.push({
        ...p,
        firm_name: c.firm_name,
        firm_short_name: c.firm_short_name,
        _collection: c,
      }));
    });
    return list.sort((a, b) => new Date(b.promise_date) - new Date(a.promise_date));
  }, [collections]);

  return (
    <DataTableContainer
      title=""
      columns={TABLE_COLUMNS.paymentPromises}
      data={allPromises}
      loading={loading}
      showAddButton={false}
      exportButton={false}
      searchPlaceholder={SEARCH_CONFIGS.paymentPromises.placeholder}
      searchFields={SEARCH_CONFIGS.paymentPromises.fields}
      showFilters={false}
      rowActions={[]}
      hideDefaultActions={true}
      entityType="paymentPromise"
      onRowClick={(row) => onFirmClick(row._collection)}
    />
  );
};

const TahsilatTabs = ({
  collections,
  loading,
  onManage,
  onImport,
  getRowSx,
  page = 1,
  pageSize = 20,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  setExtraWhereCondition,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const reminderCount = useMemo(() =>
    collections.reduce((s, c) => s + (c.reminders || []).length, 0)
  , [collections]);

  const promiseCount = useMemo(() =>
    collections.reduce((s, c) => s + (c.payment_promises || []).length, 0)
  , [collections]);

  // Translate DataTable filter/search UI into a Hasura `_bool_exp` and feed
  // the useCollections hook. Same pattern as DynamicCrudPage / WorkflowPage.
  const handleFiltersChange = useCallback(
    (uiFilters, uiSearch) => {
      if (!setExtraWhereCondition) return;
      const where = buildHasuraWhere({
        filters: uiFilters,
        searchValue: uiSearch,
        searchFields: SEARCH_CONFIGS.collections?.fields || [],
        filterConfigs: [],
      });
      setExtraWhereCondition(
        Object.keys(where).length > 0 ? where : null
      );
    },
    [setExtraWhereCondition]
  );

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': { minHeight: 44, fontSize: 13, fontWeight: 600, textTransform: 'none', py: 0 },
          }}
        >
          <Tab label="Firmalar" />
          <Tab
            label={
              <Stack direction="row" alignItems="center" gap={0.75}>
                <NotificationsNoneIcon sx={{ fontSize: 15 }} />
                Hatırlatıcılar
                {reminderCount > 0 && (
                  <Chip onClick={() => {}} label={reminderCount} size="small" sx={{ height: 16, fontSize: 10, fontWeight: 700, ml: 0.25 }} />
                )}
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" gap={0.75}>
                <HandshakeIcon sx={{ fontSize: 15 }} />
                Ödeme Sözleri
                {promiseCount > 0 && (
                  <Chip onClick={() => {}} label={promiseCount} size="small" sx={{ height: 16, fontSize: 10, fontWeight: 700, ml: 0.25 }} />
                )}
              </Stack>
            }
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <DataTableContainer
          title={t('tahsilat.title', 'Tahsilat Yönetimi')}
          columns={TABLE_COLUMNS.collections}
          data={collections}
          loading={loading}
          showAddButton={true}
          addButtonText={t('tahsilat.import.button', 'Luca İmport')}
          addButtonIcon={<FileUploadIcon />}
          addButtonSx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          onAddButtonClick={onImport}
          searchPlaceholder="tahsilat.search"
          searchFields={SEARCH_CONFIGS.collections.fields}
          rowActions={ROW_ACTIONS.collections}
          onRowAction={(actionType, row) => {
            if (actionType === 'manage') { onManage(row); return true; }
            return false;
          }}
          exportButton={true}
          exportFilename="tahsilat"
          exportLabel="tahsilat.export"
          hideDefaultActions={true}
          entityType="collection"
          getRowSx={getRowSx}
          serverPagination
          serverPage={page}
          serverPageSize={pageSize}
          serverTotalCount={totalCount}
          onServerPageChange={onPageChange}
          onServerPageSizeChange={onPageSizeChange}
          serverFilters={!!setExtraWhereCondition}
          onFiltersChange={handleFiltersChange}
        />
      )}
      {activeTab === 1 && (
        <RemindersTab collections={collections} loading={loading} onFirmClick={onManage} />
      )}
      {activeTab === 2 && (
        <PaymentPromisesTab collections={collections} loading={loading} onFirmClick={onManage} />
      )}
    </Box>
  );
};

export default TahsilatTabs;
